import React, { useMemo } from 'react';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LoadingComponent from './loading';
import useJobPolling from './hooks/useJobPolling';

const API_BASE = 'https://api.insightos.co.uk';

function SummaryRow({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</p>
      <p className="mt-1 text-lg font-semibold text-white">{value}</p>
    </div>
  );
}

export default function AvenorJobProgress({ queueType = 'leads' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams();
  const {
    title = queueType === 'advert' ? 'Generating advert' : 'Processing Avenor job',
    backPath = '/avenor',
    returnLabel = 'Return to Avenor',
  } = location.state || {};

  const statusUrl = queueType === 'advert' ? `/api/advertjobs/${jobId}` : `/api/leadsjobs/${jobId}`;
  const { error, job, progress, stage, status, isComplete, isFailed } = useJobPolling(jobId, {
    enabled: Boolean(jobId),
    statusUrl,
  });

  const recentLogs = Array.isArray(job?.logs) ? job.logs.slice(-8) : [];
  const result = job?.result || null;
  const videoUrl = result?.videoUrl
    ? (String(result.videoUrl).startsWith('http') ? result.videoUrl : `${API_BASE}${result.videoUrl}`)
    : '';
  const downloadUrl = result?.downloadUrl
    ? (String(result.downloadUrl).startsWith('http') ? result.downloadUrl : `${API_BASE}${result.downloadUrl}`)
    : '';

  const leadSummary = useMemo(() => {
    if (!result || queueType !== 'leads') {
      return [];
    }

    if (typeof result.total === 'number') {
      return [
        { label: 'Leads Found', value: result.total },
        { label: 'Inserted', value: result.insertedCount ?? 0 },
        { label: 'Skipped', value: result.skippedCount ?? 0 },
      ];
    }

    if (typeof result.processed === 'number') {
      return [
        { label: 'Processed', value: result.processed },
        { label: 'Enriched', value: result.enrichedCount ?? 0 },
        { label: 'Credits Spent', value: result.credits?.spent ?? 0 },
        { label: 'Remaining Credits', value: result.credits?.salesCredits ?? result.credits?.salesCredit ?? 0 },
      ];
    }

    if (typeof result.emailsSent === 'number') {
      return [
        { label: 'Emails Sent', value: result.emailsSent },
        { label: 'Stage', value: result.emailState?.emailStage ?? '-' },
        { label: 'Total Sent', value: result.emailState?.emailSent ?? '-' },
        { label: 'Remaining Today', value: result.emailState?.remainingToday ?? '-' },
      ];
    }

    return [];
  }, [queueType, result]);

  if (isFailed) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-6">
        <div className="max-w-xl w-full rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="rounded-2xl bg-red-500/20 p-3">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-300" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Job failed</h1>
              <p className="text-sm text-white/60">The queue worker reported an error for this Avenor request.</p>
            </div>
          </div>

          <div className="rounded-2xl bg-black/30 border border-white/10 p-4 text-sm text-white/80 whitespace-pre-wrap">
            {error || job?.failedReason || 'An unknown error occurred.'}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => navigate(backPath, { replace: true, state: { avenorRefreshKey: Date.now() } })}
              className="inline-flex items-center gap-2 rounded-xl bg-white text-slate-950 px-4 py-2 font-medium hover:bg-slate-200 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isComplete) {
    return (
      <LoadingComponent
        label={title}
        progress={progress}
        stage={stage || status}
        detail={jobId ? `Job ${jobId}` : 'Preparing job'}
        logs={recentLogs}
        showProcessDocAnimation
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white px-6 py-12">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-cyan-500/15 via-slate-950 to-emerald-500/10 p-8 shadow-2xl">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-cyan-300">Avenor Queue</p>
              <h1 className="mt-3 text-3xl font-semibold text-white">Completed</h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-300">
                {queueType === 'advert'
                  ? 'Your advert generation finished. Review the output below or download the generated video.'
                  : 'Your queue job finished. Review the processed totals below before returning to Avenor.'}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate(backPath, { replace: true, state: { avenorRefreshKey: Date.now() } })}
                className="inline-flex items-center gap-2 rounded-xl bg-white text-slate-950 px-4 py-2 font-medium hover:bg-slate-200 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                {returnLabel}
              </button>
              {downloadUrl ? (
                <a
                  href={downloadUrl}
                  className="inline-flex items-center rounded-xl border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 font-medium text-cyan-100 hover:bg-cyan-400/20 transition-colors"
                >
                  Download Video
                </a>
              ) : null}
            </div>
          </div>
        </div>

        {queueType === 'advert' && videoUrl ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-2xl backdrop-blur-xl">
            <p className="mb-3 text-sm font-semibold text-cyan-200">Advert Preview</p>
            <video controls className="w-full rounded-2xl bg-black" src={videoUrl} />
          </div>
        ) : null}

        {leadSummary.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {leadSummary.map((item) => (
              <SummaryRow key={item.label} label={item.label} value={item.value} />
            ))}
          </div>
        ) : null}

        {Array.isArray(recentLogs) && recentLogs.length > 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-2xl backdrop-blur-xl">
            <p className="mb-3 text-sm font-semibold text-cyan-200">Recent Log</p>
            <div className="space-y-2 font-mono text-xs text-slate-200">
              {recentLogs.map((logLine, index) => (
                <p key={`${index}-${logLine}`} className="break-words">{logLine}</p>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}